import React, { useEffect, useState } from 'react';

// SIGNALS — the mission's watch feed, ported from legacy/app.js (2026-08-23,
// final phase). The .sig-* dress in legacy/style.css applies unchanged.
//
// The two pickers are React too (below): the source picker (schema + narrow
// + consent handoff) and the attach menu.

function signalDayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(d)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}
const signalTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

// Flatten this mission's actions for the attach picker — actions only.
function missionActions(items, out = []) {
  for (const it of (items || [])) {
    if ((it.kind || it.schema_name) === 'action') out.push(it);
    missionActions(it.children || it.items || [], out);
  }
  return out;
}

// Attach a loose signal to one action — a person's decision, never a guess.
function AttachMenu({ sig, onClose, refresh }) {
  const ref = React.useRef(null);
  const [rel, setRel] = useState('evidenced_by');
  useEffect(() => {
    const close = (ev) => { if (ref.current && !ref.current.contains(ev.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [onClose]);
  const actions = missionActions(window.currentMissionItems?.() || []);
  return (
    <div ref={ref} className="sig-attach-menu">
      {!actions.length ? (
        <div className="sig-menu-empty">No actions on this mission yet.</div>
      ) : (
        <>
          <div className="sig-menu-rels">
            {[['evidenced_by', 'evidences'], ['influenced_by', 'influences']].map(([key, label]) => (
              <button key={key} className={`sig-menu-rel${rel === key ? ' on' : ''}`}
                      onClick={() => setRel(key)}>{label}</button>
            ))}
          </div>
          <div className="sig-menu-list">
            {actions.map((a) => (
              <div key={a.id} className="sig-menu-opt"
                   onClick={async () => {
                     onClose();
                     try {
                       await window.postJson('/api/signals/attach', { action_id: a.id, node_id: sig.node_id, relation: rel });
                       refresh();
                     } catch (e) { alert(`Couldn't attach — ${e.message}`); }
                   }}>
                {a.title || a.name || '(untitled)'}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Pick a schema, narrow it, save — then always hand off to the consent flow
// (/signals/connect answers 409 already_granted and passes straight through).
function SourcePicker({ missionId, existing, onClose }) {
  const [schemas, setSchemas] = useState(null);
  const [error, setError] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [preds, setPreds] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        let { schemas } = await window.fetchJson('/api/signals/schemas');
        schemas = schemas || [];
        let ch = null;
        let pr = [];
        if (existing) {
          ch = schemas.find((sc) => sc.schema_name === existing.schema_name)
            || { schema_name: existing.schema_name, fields: null };
          if (!schemas.includes(ch)) schemas = [ch, ...schemas];
          pr = (existing.predicates || []).map((p) => ({ ...p }));
          if (!ch.fields) {
            try {
              const detail = await window.fetchJson(`/api/signals/schemas/${encodeURIComponent(ch.schema_name)}/fields`);
              ch = { ...ch, fields: detail.fields || [], event_time_field: detail.event_time_field || existing.event_time_field || null };
              schemas = schemas.map((sc) => (sc.schema_name === ch.schema_name ? ch : sc));
            } catch { ch = { ...ch, fields: [] }; }
          }
        }
        if (!live) return;
        setSchemas(schemas);
        setChosen(ch);
        setPreds(pr);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, []);

  const pickSchema = async (sc) => {
    setPreds([]);
    if (sc.fields) { setChosen(sc); if (sc.fields.length) setPreds([{ field: sc.fields[0].name, op: 'is', value: '' }]); return; }
    setChosen(sc);
    try {
      const detail = await window.fetchJson(`/api/signals/schemas/${encodeURIComponent(sc.schema_name)}/fields`);
      const full = { ...sc, fields: detail.fields || [], event_time_field: detail.event_time_field || null };
      setSchemas((cur) => cur.map((x) => (x.schema_name === sc.schema_name ? full : x)));
      setChosen(full);
      setPreds(full.fields.length ? [{ field: full.fields[0].name, op: 'is', value: '' }] : []);
    } catch {
      setChosen({ ...sc, fields: [] });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        anchor_node_id: missionId,
        schema_name: chosen.schema_name,
        predicates: preds.filter((p) => String(p.value).trim()),
        event_time_field: chosen.event_time_field,
      };
      const res = existing
        ? await window.fetchJson(`/api/signals/sources/${existing.id}`,
            { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
        : await window.postJson('/api/signals/sources', payload);
      window.location.href = `/signals/connect?source_id=${res.source.id}`;
    } catch (e) {
      setSaving(false);
      alert(e.status === 409
        ? `You're already watching ${chosen.schema_name} on this mission. Edit that source instead of adding a second one.`
        : `Couldn't save — ${e.message}`);
    }
  };

  return (
    <div className="sig-picker-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sig-picker">
        <div className="breadcrumb-detail">{existing ? 'Edit signal source' : 'Add signal source'}</div>
        <h2>What should this mission watch?</h2>
        <div className="sig-picker-body">
          {error && <div className="sig-loading">Couldn't load schemas — {error}</div>}
          {!error && !schemas && <div className="sig-loading">Loading schemas…</div>}
          {!error && schemas && (
            <>
              <div className="sheet-section">
                <div className="sheet-section-title">Available</div>
                <div className="sig-pick-list">
                  {schemas.map((sc) => (
                    <div key={sc.schema_name}
                         className={`sig-pick-row${chosen && chosen.schema_name === sc.schema_name ? ' on' : ''}`}
                         onClick={() => pickSchema(sc)}>
                      <span className="sig-pick-name">{sc.schema_name}</span>
                    </div>
                  ))}
                </div>
              </div>
              {chosen && (
                <>
                  <div className="sheet-section">
                    <div className="sheet-section-title">Narrow it</div>
                    {!chosen.fields && <div className="sig-hint">Loading fields…</div>}
                    {chosen.fields && !chosen.fields.length && (
                      <div className="sig-hint">No filterable fields on this schema — nested and encrypted fields can’t be filtered, so the whole schema will be watched.</div>
                    )}
                    {(chosen.fields ? preds : []).map((p, i) => (
                      <div className="sig-pred-row" key={i}>
                        <span className="sig-pred-join">{i === 0 ? 'where' : 'and'}</span>
                        <select className="sheet-select" value={p.field}
                                onChange={(e) => setPreds((cur) => cur.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
                          {chosen.fields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                        </select>
                        <select className="sheet-select sig-op" value={p.op}
                                onChange={(e) => setPreds((cur) => cur.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)))}>
                          {[['is', 'is'], ['is_not', 'is not'], ['starts_with', 'starts with'], ['contains', 'contains']]
                            .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                        <input type="text" className="sheet-select sig-val" placeholder="value" value={p.value}
                               onChange={(e) => setPreds((cur) => cur.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                        <button className="link-x" onClick={() => setPreds((cur) => cur.filter((_, j) => j !== i))}>×</button>
                      </div>
                    ))}
                    {chosen.fields && chosen.fields.length ? (
                      <button className="sig-pred-add"
                              onClick={() => setPreds((cur) => [...cur, { field: chosen.fields[0].name, op: 'is', value: '' }])}>
                        + condition
                      </button>
                    ) : null}
                  </div>
                  <div className="sig-confirm-bar">
                    <button className="sig-confirm-btn" disabled={saving} onClick={save}>
                      {saving ? 'Saving…' : (existing ? 'Save changes' : 'Watch this')}
                    </button>
                    <span className="sig-confirm-cost">Missions may ask Paradigm for read access to {chosen.schema_name}.</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceChip({ b, report, refresh, onEdit }) {
  const preds = b.predicates || [];
  const phrase = (p) => `${p.field} ${p.op.replace(/_/g, ' ')} ${p.value}`;
  const r = report && report[b.id];
  let cls = 'sig-source editable';
  let title = (preds.length
    ? `${b.schema_name} where ${preds.map(phrase).join(' and ')} — click to edit`
    : `${b.schema_name} — everything. Click to edit`)
    + (b.anchor_title ? `\nOn the goal “${b.anchor_title}”` : '');
  if (r && r.error) { cls += ' needs-connect'; title = `Couldn't read ${b.schema_name} — ${r.error}`; }
  else if (r && r.readable === false) { cls += ' needs-connect'; title = `Missions can't read ${b.schema_name} yet — click to allow it`; }
  else if (r && r.matched === 0) { cls += ' needs-connect'; title = `Matched 0 of ${r.scanned} ${b.schema_name} record(s) — check the condition. "is" matches exactly, including capitals.`; }
  return (
    <span className={cls} title={title} onClick={onEdit}>
      <span className="sig-source-name">{b.schema_name}</span>
      {b.anchor_title ? <span className="sig-source-on">{b.anchor_title}</span> : null}
      <button className="sig-source-x" title="Stop watching"
              onClick={async (ev) => {
                ev.stopPropagation();
                if (!confirm(`Stop watching ${b.schema_name}?\n\nIts entries leave this mission's feed. The ${b.schema_name} records themselves aren't touched.`)) return;
                try {
                  await window.fetchJson(`/api/signals/sources/${b.id}`, { method: 'DELETE' });
                  refresh();
                } catch (e) { alert(`Couldn't remove — ${e.message}`); }
              }}>×</button>
    </span>
  );
}

function SignalRow({ sig, refresh, attachOpen, onToggleAttach }) {
  return (
    <div>
      <div className="sig-row">
        <span className="sig-glyph">◈</span>
        <span className="sig-kind">{sig.kind || ''}</span>
        <span className="sig-title">{sig.title}</span>
        <span className="sig-schema">{sig.schema_name}</span>
        <span className="sig-time">{signalTime(sig.occurred_at)}</span>
      </div>
      <div className={`sig-attr${sig.attached ? '' : ' loose'}`}>
        <span className="arrow">↳</span>
        {sig.attached ? (
          <>
            <span className="verb">{sig.attached.relation === 'influenced_by' ? 'influences' : 'evidences'}</span>
            <span className="target">{sig.attached.action_title}</span>
            <button className="sig-detach" title="Unattach"
                    onClick={async () => {
                      try {
                        await window.fetchJson(`/api/signals/attach/${sig.attached.rel_id}`, { method: 'DELETE' });
                        refresh();
                      } catch (e) { alert(`Couldn't unattach — ${e.message}`); }
                    }}>×</button>
          </>
        ) : (
          <>
            <span className="verb">not attributed</span>
            <button className="sig-attach" onClick={onToggleAttach}>
              attach to an action ▾
            </button>
            {attachOpen && <AttachMenu sig={sig} onClose={onToggleAttach} refresh={refresh} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function Signals({ mission }) {
  const missionId = mission.id;
  const [state, setState] = useState({ loading: true });
  const [pages, setPages] = useState([]); // extra "show earlier" pages
  const [cursor, setCursor] = useState(null);
  const [moreLabel, setMoreLabel] = useState('show earlier ↓');
  const [epoch, setEpoch] = useState(0);
  const [picker, setPicker] = useState(null);      // { existing } | null
  const [attachFor, setAttachFor] = useState(null); // signal node_id
  const refresh = () => { setPages([]); setAttachFor(null); setEpoch((n) => n + 1); };

  useEffect(() => {
    const again = () => refresh();
    document.addEventListener('missions:refresh-signals', again);
    return () => document.removeEventListener('missions:refresh-signals', again);
  }, []);

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    Promise.all([
      window.fetchJson(`/api/signals/sources?mission=${missionId}`),
      window.fetchJson(`/api/signals/feed?mission=${missionId}`),
    ]).then(([{ sources }, feed]) => {
      if (!live) return;
      setState({ loading: false, sources: sources || [], feed });
      // Same bridge as Schedule: the agent should be able to reason about the
      // evidence the user is looking at, on any surface, without a second fetch.
      window.setMissionSignals?.(feed.signals || []);
      setCursor(feed.next_before || null);
      setMoreLabel('show earlier ↓');
    }).catch((e) => live && setState({ loading: false, error: e.message }));
    return () => { live = false; };
  }, [missionId, epoch]);

  if (state.loading) return <div className="sig-loading">Looking…</div>;
  if (state.error) return <div className="sig-loading">Couldn't load signals — {state.error}</div>;

  const { sources, feed } = state;
  const report = {};
  for (const d of (feed.sources || [])) report[d.source_id] = d;
  const allSignals = [...(feed.signals || []), ...pages.flat()];

  const bar = (
    <div className="sig-bar">
      <span className="sig-bar-k">watching</span>
      {sources.map((b) => (
        <SourceChip key={b.id} b={b} report={report} refresh={refresh}
                    onEdit={() => setPicker({ existing: b })} />
      ))}
      <button className="sig-source-add" onClick={() => setPicker({ existing: null })}>+ source</button>
      {picker && <SourcePicker missionId={missionId} existing={picker.existing} onClose={() => setPicker(null)} />}
    </div>
  );

  if (!sources.length) {
    return (
      <>
        {bar}
        <div className="sig-empty">
          <p>Nothing watched yet.</p>
          <p className="sub">Point this mission at something real — commits on a repo, a message thread — and what happens out there shows up here.</p>
        </div>
      </>
    );
  }

  if (!allSignals.length) {
    const ds = feed.sources || [];
    const unreadable = ds.filter((d) => d.readable === false);
    const failed = ds.filter((d) => d.error);
    let body;
    if (failed.length) {
      body = (
        <>
          <p>Couldn't read {failed.map((d) => d.schema_name).join(', ')}.</p>
          <p className="sub">{failed[0].error}</p>
        </>
      );
    } else if (unreadable.length) {
      const first = unreadable[0];
      body = (
        <>
          <p>Missions can't read {unreadable.map((d) => d.schema_name).join(', ')} yet.</p>
          <p className="sub">It needs your permission — one screen, then this fills in.</p>
          {first && first.source_id ? (
            <button className="sig-connect-btn"
                    onClick={() => { window.location.href = `/signals/connect?source_id=${encodeURIComponent(first.source_id)}`; }}>
              Allow {first.schema_name}
            </button>
          ) : null}
        </>
      );
    } else {
      const scanned = ds.reduce((n, d) => n + (d.scanned || 0), 0);
      body = (
        <>
          <p>Nothing matched.</p>
          <p className="sub">Checked {scanned} record{scanned === 1 ? '' : 's'}. Click a source above to loosen the condition — “is” matches exactly, including capitals.</p>
        </>
      );
    }
    return <>{bar}<div className="sig-empty">{body}</div></>;
  }

  // Day headings carried across pages: never re-print one a page above showed.
  const out = [];
  let currentDay = null;
  for (const sig of allSignals) {
    const label = signalDayLabel(sig.occurred_at);
    if (label !== currentDay) {
      currentDay = label;
      out.push(<div key={`day-${label}-${out.length}`} className="sig-day">{label}</div>);
    }
    out.push(
      <SignalRow key={sig.id || `${sig.occurred_at}-${out.length}`} sig={sig} refresh={refresh}
                 attachOpen={attachFor === (sig.node_id || sig.id)}
                 onToggleAttach={() => setAttachFor((cur) => (cur === (sig.node_id || sig.id) ? null : (sig.node_id || sig.id)))} />,
    );
  }

  return (
    <>
      {bar}
      {out}
      {cursor ? (
        <div className="sig-more" onClick={async () => {
          setMoreLabel('loading…');
          try {
            const page = await window.fetchJson(`/api/signals/feed?mission=${missionId}&before=${encodeURIComponent(cursor)}`);
            setPages((cur) => [...cur, page.signals || []]);
            setCursor(page.next_before || null);
            setMoreLabel('show earlier ↓');
          } catch (e) {
            setMoreLabel(`couldn't load more — ${e.message}`);
          }
        }}>{moreLabel}</div>
      ) : null}
    </>
  );
}
